import React, { useState, useEffect } from 'react';
import './LoginPage.css';
import Logo from './Logo';
import EyeIcon from './EyeIcon';

const LoginPage = ({ onBack, onNavigateToSignup, onNavigateToAbout, onNavigateToTerms, onNavigateToPrivacy }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showMobileNavMenu, setShowMobileNavMenu] = useState(false);
  const [showMobileProfileMenu, setShowMobileProfileMenu] = useState(false);

  // Close mobile menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      try {
        if (!e || !e.target) return;
        
        if (showMobileProfileMenu && !e.target.closest('.mobile-profile-icon') && !e.target.closest('.mobile-profile-menu')) {
          setShowMobileProfileMenu(false);
        }
        if (showMobileNavMenu && !e.target.closest('.mobile-hamburger') && !e.target.closest('.mobile-nav-dropdown')) {
          setShowMobileNavMenu(false);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error in click outside handler:', error);
        }
      }
    };

    try {
      document.addEventListener('click', handleClickOutside);
      return () => {
        try {
          document.removeEventListener('click', handleClickOutside);
        } catch (error) {
          // Silently handle cleanup errors
        }
      };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error setting up click outside listener:', error);
      }
      return () => {};
    }
  }, [showMobileProfileMenu, showMobileNavMenu]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const showNotification = (message) => {
    try {
      if (typeof document === 'undefined' || !document.body) return;
      if (!message) return;

      const notification = document.createElement('div');
      notification.textContent = message;
      notification.style.cssText = `
        position: fixed;
        top: 100px;
        left: 50%;
        transform: translateX(-50%) translateY(-20px);
        background-color: #2d3649;
        color: #ffffff;
        padding: 16px 32px;
        border-radius: 8px;
        font-size: 1rem;
        font-family: 'DM Sans', sans-serif;
        font-weight: 600;
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
        z-index: 9999999;
        pointer-events: none;
        white-space: nowrap;
        opacity: 0;
        transition: all 0.3s ease;
      `;

      if (document.body) {
        document.body.appendChild(notification);

        requestAnimationFrame(() => {
          try {
            if (notification && notification.style) {
              notification.style.opacity = '1';
              notification.style.transform = 'translateX(-50%) translateY(0)';
            }
          } catch (error) {
            // Silently handle animation errors
          }
        });

        setTimeout(() => {
          try {
            if (notification && notification.style) {
              notification.style.opacity = '0';
              notification.style.transform = 'translateX(-50%) translateY(-20px)';
              setTimeout(() => {
                try {
                  if (notification && notification.parentNode) {
                    notification.parentNode.removeChild(notification);
                  }
                } catch (error) {
                  // Silently handle cleanup errors
                }
              }, 300);
            }
          } catch (error) {
            // Silently handle errors
          }
        }, 3000);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error in showNotification:', error);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    showNotification('Accounts launching soon with the app!');
  };

  const handleOAuthClick = (e) => {
    e.preventDefault();
    showNotification('Accounts launching soon with the app!');
  };

  return (
    <div className="login-page">
      {/* Navigation Bar - same as signup page */}
      <nav className="navigation">
        <div className="container">
          <div className="nav-content">
            {/* Mobile Hamburger Menu */}
            <div className="mobile-nav-controls">
              <button
                className={`mobile-hamburger ${showMobileNavMenu ? 'active' : ''}`}
                onClick={() => setShowMobileNavMenu(!showMobileNavMenu)}
                aria-label="Navigation menu"
              >
                {/* Hamburger lines */}
                <span></span>
                <span></span>
                <span></span>
                {/* Logo icon - appears when active (mobile only) */}
                <img 
                  className="hamburger-logo-icon"
                  src="/Images/holologo_mobile.svg"
                  alt="DermaMatch Logo"
                />
              </button>
            </div>

            {/* Logo placement: top left */}
            <div className="nav-logo">
              <button 
                className="logo-button" 
                onClick={onBack}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
              >
                <Logo size="medium" />
              </button>
            </div>
            
            {/* Navigation items - centered */}
            <div className="nav-items">
              <button onClick={onBack} className="nav-link">
                DISCOVER
              </button>
              <button onClick={onNavigateToAbout} className="nav-link">
                ABOUT
              </button>
            </div>
            
            {/* User actions - right aligned */}
            <div className="nav-actions">
              <button className="btn btn-outline nav-btn">LOG IN</button>
              <button className="btn btn-primary nav-btn" onClick={onNavigateToSignup}>SIGN UP</button>
            </div>

            {/* Mobile Profile Icon */}
            <button
              className="mobile-profile-icon"
              onClick={() => setShowMobileProfileMenu(!showMobileProfileMenu)}
              aria-label="Profile menu"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M6 20c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {/* Mobile Nav Dropdown */}
          {showMobileNavMenu && (
            <div className="mobile-nav-dropdown">
              <button
                onClick={() => { onBack(); setShowMobileNavMenu(false); }}
              >
                DISCOVER
              </button>
              <button
                onClick={() => { onNavigateToAbout(); setShowMobileNavMenu(false); }}
              >
                ABOUT
              </button>
            </div>
          )}

          {/* Mobile Profile Menu */}
          {showMobileProfileMenu && (
            <div className="mobile-profile-menu">
              <button onClick={() => { onNavigateToSignup(); setShowMobileProfileMenu(false); }}>
                SIGN UP
              </button>
              <button onClick={() => { setShowMobileProfileMenu(false); }}>
                LOG IN
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content with Background Gradient */}
      <div className="login-main-content">
        <div className="background-gradient"></div>
        <div className="login-container">
          <div className="login-card">
            {/* Log In Title using SVG */}
            <div className="login-title">
              <svg width="91" height="29" viewBox="0 0 91 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.0694971 22V0.0479984H4.8375V17.936H15.9095V22H0.0694971ZM26.6557 22.384C24.8211 22.384 23.2637 22.0747 21.9837 21.456C20.7037 20.816 19.7331 19.8453 19.0717 18.544C18.4104 17.2213 18.0797 15.568 18.0797 13.584C18.0797 11.5573 18.4104 9.904 19.0717 8.624C19.7331 7.32267 20.7037 6.36267 21.9837 5.744C23.2637 5.104 24.8211 4.784 26.6557 4.784C28.5331 4.784 30.1011 5.104 31.3597 5.744C32.6397 6.36267 33.6104 7.32267 34.2717 8.624C34.9331 9.904 35.2637 11.5573 35.2637 13.584C35.2637 15.568 34.9331 17.2213 34.2717 18.544C33.6104 19.8453 32.6397 20.816 31.3597 21.456C30.1011 22.0747 28.5331 22.384 26.6557 22.384ZM26.6557 18.928C27.6371 18.928 28.4157 18.7467 28.9917 18.384C29.5891 18.0213 30.0264 17.4667 30.3037 16.72C30.5811 15.9733 30.7197 15.056 30.7197 13.968V13.2C30.7197 12.112 30.5811 11.1947 30.3037 10.448C30.0264 9.70133 29.5891 9.14667 28.9917 8.784C28.4157 8.4 27.6371 8.208 26.6557 8.208C25.6957 8.208 24.9171 8.4 24.3197 8.784C23.7437 9.14667 23.3171 9.70133 23.0397 10.448C22.7624 11.1947 22.6237 12.112 22.6237 13.2V13.968C22.6237 15.056 22.7624 15.9733 23.0397 16.72C23.3171 17.4667 23.7437 18.0213 24.3197 18.384C24.9171 18.7467 25.6957 18.928 26.6557 18.928ZM41.6087 28.016C40.8621 28.016 40.1581 27.856 39.4967 27.536C38.8354 27.2373 38.3021 26.7893 37.8967 26.192C37.4914 25.5947 37.2887 24.88 37.2887 24.048C37.2887 23.1093 37.5554 22.3627 38.0887 21.808C38.6434 21.232 39.2621 20.8053 39.9447 20.528C39.4541 20.2507 39.0487 19.888 38.7287 19.44C38.4301 18.992 38.2807 18.4693 38.2807 17.872C38.2807 17.04 38.5794 16.368 39.1767 15.856C39.7954 15.3227 40.4994 14.9813 41.2887 14.832C40.4994 14.3413 39.8807 13.7227 39.4327 12.976C39.0061 12.208 38.7927 11.3547 38.7927 10.416C38.7927 9.2 39.0914 8.176 39.6887 7.344C40.3074 6.512 41.1821 5.88267 42.3127 5.456C43.4647 5.008 44.8194 4.784 46.3767 4.784C46.9954 4.784 47.5714 4.816 48.1047 4.88C48.6594 4.944 49.1607 5.05067 49.6087 5.2C50.3981 4.73067 50.9314 4.21867 51.2087 3.664C51.5074 3.088 51.6674 2.62933 51.6887 2.288H55.8167C55.8167 3.07733 55.6674 3.76 55.3687 4.336C55.0914 4.912 54.6861 5.392 54.1527 5.776C53.6194 6.13867 52.9794 6.40533 52.2327 6.576C52.7874 7.04533 53.2141 7.61067 53.5127 8.272C53.8114 8.93333 53.9607 9.648 53.9607 10.416C53.9607 11.6107 53.6727 12.6347 53.0967 13.488C52.5207 14.32 51.6994 14.96 50.6327 15.408C49.5874 15.856 48.3287 16.08 46.8567 16.08H44.2967C43.7847 16.08 43.4007 16.176 43.1447 16.368C42.8887 16.5387 42.7607 16.7947 42.7607 17.136C42.7607 17.4133 42.8674 17.6587 43.0807 17.872C43.3154 18.064 43.6887 18.16 44.2007 18.16H51.2087C52.6167 18.16 53.7367 18.5973 54.5687 19.472C55.4221 20.3253 55.8487 21.424 55.8487 22.768C55.8487 23.792 55.5821 24.6987 55.0487 25.488C54.5154 26.2773 53.7794 26.896 52.8407 27.344C51.9021 27.792 50.8247 28.016 49.6087 28.016H41.6087ZM43.4647 25.04H49.3847C49.7687 25.04 50.1101 24.9653 50.4087 24.816C50.7074 24.688 50.9421 24.496 51.1127 24.24C51.2834 24.0053 51.3687 23.728 51.3687 23.408C51.3687 22.8533 51.1981 22.4373 50.8567 22.16C50.5154 21.8827 50.0887 21.744 49.5767 21.744H43.4647C42.9527 21.744 42.5261 21.8933 42.1847 22.192C41.8647 22.4907 41.7047 22.8853 41.7047 23.376C41.7047 23.8667 41.8647 24.2613 42.1847 24.56C42.5261 24.88 42.9527 25.04 43.4647 25.04ZM46.3767 13.2C47.4221 13.2 48.2114 12.9653 48.7447 12.496C49.2994 12.0053 49.5767 11.312 49.5767 10.416C49.5767 9.52 49.2994 8.83733 48.7447 8.368C48.2114 7.87733 47.4221 7.632 46.3767 7.632C45.3527 7.632 44.5634 7.87733 44.0087 8.368C43.4754 8.83733 43.2087 9.52 43.2087 10.416C43.2087 10.992 43.3261 11.4933 43.5607 11.92C43.7954 12.3467 44.1474 12.6667 44.6167 12.88C45.0861 13.0933 45.6727 13.2 46.3767 13.2ZM65.5995 22V0.0479984H70.3675V22H65.5995ZM75.1605 22V5.168H78.8405L79.1925 7.472H79.4165C79.8005 6.91733 80.2592 6.43733 80.7925 6.032C81.3472 5.62667 81.9658 5.31733 82.6485 5.104C83.3312 4.89067 84.0778 4.784 84.8885 4.784C85.9978 4.784 86.9685 4.98667 87.8005 5.392C88.6325 5.79733 89.2725 6.43733 89.7205 7.312C90.1898 8.18667 90.4245 9.328 90.4245 10.736V22H85.9765V11.44C85.9765 10.9067 85.9125 10.4587 85.7845 10.096C85.6565 9.712 85.4645 9.40267 85.2085 9.168C84.9738 8.912 84.6752 8.73067 84.3125 8.624C83.9498 8.51733 83.5445 8.464 83.0965 8.464C82.4352 8.464 81.8378 8.624 81.3045 8.944C80.7712 9.264 80.3552 9.70133 80.0565 10.256C79.7578 10.8107 79.6085 11.4507 79.6085 12.176V22H75.1605Z" fill="#2D3648"/>
              </svg>
            </div>
            
            {/* OAuth Icons */}
            <div className="oauth-section">
              <div className="oauth-icons">
                <button className="oauth-btn google-btn" onClick={handleOAuthClick}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                </button>
                <button className="oauth-btn apple-btn" onClick={handleOAuthClick}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" fill="#000000"/>
                  </svg>
                </button>
                <button className="oauth-btn microsoft-btn" onClick={handleOAuthClick}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1" y="1" width="10.5" height="10.5" fill="#F25022"/>
                    <rect x="12.5" y="1" width="10.5" height="10.5" fill="#7FBA00"/>
                    <rect x="1" y="12.5" width="10.5" height="10.5" fill="#00A4EF"/>
                    <rect x="12.5" y="12.5" width="10.5" height="10.5" fill="#FFB900"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="separator">
              <span className="separator-text">OR</span>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email"
                  className="form-field"
                />
              </div>
              <div className="form-group">
                <div className="password-field-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Password"
                    className="form-field"
                  />
                  <EyeIcon 
                    isVisible={showPassword}
                    onClick={() => setShowPassword(!showPassword)}
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-primary login-btn">
                LOG IN
              </button>
            </form>

            {/* Terms and Conditions */}
            <p className="terms-text">
              By logging in, you agree to our{' '}
              <a 
                href="#" 
                className="terms-link" 
                onClick={(e) => { e.preventDefault(); if(onNavigateToTerms) onNavigateToTerms(); }}
              >
                Terms of Service
              </a>
              {' '}and{' '}
              <a 
                href="#" 
                className="terms-link" 
                onClick={(e) => { e.preventDefault(); if(onNavigateToPrivacy) onNavigateToPrivacy(); }}
              >
                Privacy Policy
              </a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
